import { connect } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next"
import { toast } from "react-toastify";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Col,
    Row,
    Label
} from "reactstrap";
import Select from 'react-select';
import EntityRoutingService from "src/modules/data-protection/apis/EntityRoutingService";
import AdminstrationSettingsService from "../../api/adminstrationSettingsService";
import UserUtils from "src/services/utils/UserUtils";
import { useCallback, useEffect, useState } from "react";

const RoutingModal = ({
    currentUser,
    t,
    toggle,
    show,
    onComplete,
    prevValues
}) => {
    const isEditing = !!prevValues?.legal_entity?.id;

    const [ entity, setEntity ] = useState(null);
    const [ routingSettings, setRoutingSettings ] = useState({
        defaultSoftwareOwner: null,
        defaultSoftwareRiskOwner: null,
        defaultSoftwareTaskOwner: null,

        defaultHardwareOwner: null,
        defaultHardwareRiskOwner: null,
        defaultHardwareTaskOwner: null,

        defaultIncidentOwner: null,
        defaultIncidentRiskOwner: null,
        defaultIncidentTaskOwner: null,

        defaultDsrOwner: null,
        defaultDsrTaskOwner: null
    });

    const [ errors, setErrors ] = useState({
        entity: false
    })

    const userUtils = new UserUtils();
    const entityService = EntityRoutingService.getInstance();
    const commonService = AdminstrationSettingsService.getInstance();
    
    const isAdmin = userUtils.isAnalystAdmin(currentUser);

    const entitiesQuery = useQuery({
        queryKey: [
            "data-protection-fetch-settings-not-routed-entities-list"
        ],
        queryFn: async () => {
            const response = await entityService.fetchUnRoutedEntitiesList({
                pageIndex: 1,
                pageSize: 1000
            });

            return (response.legalEntities || []).map((entity) => {
                return {
                    value: entity.id,
                    label: `${entity.title} (${entity.shortName})`
                }
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: show,
        onError: () => {
            toast(t("An error occurred while fetching entities."), {
                type: "error"
            });
        },
    });

    const {
        data: analysts,
        isFetching: analystsAreFetching
    } = useQuery({
        queryKey: ["fetch-analyst-and-admin-analyst-list"],
        queryFn: async () => {
            const response = await commonService.FetchAnalystsAndAnalystAdmins();

            return (response || []).map((user) => {
                return {
                    value: user.id,
                    label: `${user.first_name} ${user.last_name}`
                }
            })
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching analyst admins list."), {
                type: "error"
            });
        },
    });

    const saveSettingsMutation = useMutation({
        mutationFn: async () => {
            return await entityService.saveRouterSettings(entity.value, Object.keys(routingSettings).reduce((acc, key) => {
                acc[key] = routingSettings[key]?.value || null;
                return acc;
            }, {}));
        },
        onError: () => {
            toast(t("An error occurred while saving settings."), {
                type: "error"
            });
        },
        onSuccess: () => {
            toast(t("Settings saved successfully."), {
                type: "success"
            });

            onComplete();
            toggle();
        }
    });

    const fetchRoutingDetailsMutation = useMutation({
        mutationFn: async (id) => {
            return await entityService.fetchRoutingDetails(id);
        },
        onError: () => {
            toast(t("An error occurred while fetching routing details."), {
                type: "error"
            });
        },
        onSuccess: (data) => {
            if (Array.isArray(data.data)) {
                setRoutingSettings({
                    defaultSoftwareOwner: null,
                    defaultSoftwareRiskOwner: null,
                    defaultSoftwareTaskOwner: null,

                    defaultHardwareOwner: null,
                    defaultHardwareRiskOwner: null,
                    defaultHardwareTaskOwner: null,

                    defaultIncidentOwner: null,
                    defaultIncidentRiskOwner: null,
                    defaultIncidentTaskOwner: null,

                    defaultDsrOwner: null,
                    defaultDsrTaskOwner: null
                });
            }
            else{
                if(data?.data && typeof data.data === 'object'){
                    setRoutingSettings({
                        defaultSoftwareOwner: {
                            value: data.data.default_software_owner?.id,
                            label: data.data.default_software_owner?.full_name
                        },
                        defaultSoftwareRiskOwner: {
                            value: data.data.default_software_risk_owner?.id,
                            label: data.data.default_software_risk_owner?.full_name
                        },
                        defaultSoftwareTaskOwner: {
                            value: data.data.default_software_task_owner?.id,
                            label: data.data.default_software_task_owner?.full_name
                        },

                        defaultHardwareOwner: {
                            value: data.data.default_hardware_owner?.id,
                            label: data.data.default_hardware_owner?.full_name
                        },
                        defaultHardwareRiskOwner: {
                            value: data.data.default_hardware_risk_owner?.id,
                            label: data.data.default_hardware_risk_owner?.full_name
                        },
                        defaultHardwareTaskOwner: {
                            value: data.data.default_hardware_task_owner?.id,
                            label: data.data.default_hardware_task_owner?.full_name
                        },

                        defaultIncidentOwner: {
                            value: data.data.default_incident_owner?.id,
                            label: data.data.default_incident_owner?.full_name
                        },
                        defaultIncidentRiskOwner: {
                            value: data.data.default_incident_risk_owner?.id,
                            label: data.data.default_incident_risk_owner?.full_name
                        },
                        defaultIncidentTaskOwner: {
                            value: data.data.default_incident_task_owner?.id,
                            label: data.data.default_incident_task_owner?.full_name
                        },

                        defaultDsrOwner: {
                            value: data.data.default_dsr_owner?.id,
                            label: data.data.default_dsr_owner?.full_name
                        },
                        defaultDsrTaskOwner: {
                            value: data.data.default_dsr_task_owner?.id,
                            label: data.data.default_dsr_task_owner?.full_name
                        }
                    });
                }
            }
        }
    });

    const handleRoutingChange = useCallback((settingKey, value) => {
        setRoutingSettings((currentState) => {
            return {
                ...currentState,
                [settingKey]: value
            }
        });

        setErrors((currentState) => {
            return {
                ...currentState,
                [settingKey]: false
            }
        })
    }, []);

    const handleSaveSettings = () => {
        const currentErrors = {
            entity: !isEditing ? !entity?.value : false
        }; 

        // Object.keys(routingSettings).forEach((key) => {
        //     currentErrors[key] = !routingSettings[key]?.value
        // });

        setErrors(currentErrors);

        if(!Object.keys(currentErrors).filter((key) => currentErrors[key]).length){
            saveSettingsMutation.mutate();
        }
    }

    useEffect(() => {
        if(show){
            setEntity(null);
            setRoutingSettings({
                defaultSoftwareOwner: null,
                defaultSoftwareRiskOwner: null,
                defaultSoftwareTaskOwner: null,

                defaultHardwareOwner: null,
                defaultHardwareRiskOwner: null,
                defaultHardwareTaskOwner: null,

                defaultIncidentOwner: null,
                defaultIncidentRiskOwner: null,
                defaultIncidentTaskOwner: null,

                defaultDsrOwner: null,
                defaultDsrTaskOwner: null
            });
            setErrors({
                entity: false,
            })
        }
    }, [ show ]);

    useEffect(() => {
        if(prevValues?.legal_entity) {
            setEntity({
                value: prevValues.legal_entity.id,
                label: prevValues.legal_entity.title
            });
            
            fetchRoutingDetailsMutation.mutate(prevValues.legal_entity.id);
        }
    }, [ prevValues ])

    return (
        <Modal 
            size="lg" 
            isOpen={show}
        >
            <ModalHeader 
                className="border-0" 
                toggle={toggle}
            >
                {t(isEditing ? "Edit Routing Settings" : "Add Routing Settings")}
            </ModalHeader>

            <ModalBody>
                <Row className="mb-3">
                    <Col sm='12' className="mb-3">
                        <Label>    
                            {t("Entity")}
                        </Label>
                        {!isEditing ? (
                            <Select
                                options={entitiesQuery.data || []}
                                isLoading={entitiesQuery.isFetching}
                                value={routingSettings.entity}
                                onChange={(e) => {
                                    setEntity(e);
                                    setErrors((currentState) => {
                                        return {
                                            ...currentState,
                                            entity: false
                                        }
                                    })
                                }}
                                placeholder={t("Select Entity")}
                                isDisabled={!isAdmin || entitiesQuery.isFetching}
                            />
                        ) : (
                            <p className="mb-0">
                                {prevValues.legal_entity.title}
                            </p>
                        )}
                        {errors.entity && (
                            <span className="invalid-feedback d-block">
                                {t('This field is required')}
                            </span>
                        )}
                    </Col>

                    <h5 className="mt-2">
                        <i className="ri-arrow-right-s-fill font-size-22 align-middle" />
                        {t('Software Assets')}
                    </h5>

                    <Col style={{
                        marginLeft: '24px'
                    }}>
                        <Row>
                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Assets To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultSoftwareOwner}
                                    onChange={(e) => handleRoutingChange('defaultSoftwareOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultSoftwareOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>

                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Risks To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultSoftwareRiskOwner}
                                    onChange={(e) => handleRoutingChange('defaultSoftwareRiskOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultSoftwareRiskOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>

                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Tasks To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultSoftwareTaskOwner}
                                    onChange={(e) => handleRoutingChange('defaultSoftwareTaskOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultSoftwareTaskOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>
                        </Row>
                    </Col>

                    <h5 className="mt-2">
                        <i className="ri-arrow-right-s-fill font-size-22 align-middle" />
                        {t('Hardware Assets')}
                    </h5>

                    <Col style={{
                        marginLeft: '24px'
                    }}>
                        <Row>
                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Assets To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultHardwareOwner}
                                    onChange={(e) => handleRoutingChange('defaultHardwareOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultHardwareOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>

                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Risks To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultHardwareRiskOwner}
                                    onChange={(e) => handleRoutingChange('defaultHardwareRiskOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultHardwareRiskOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>

                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Tasks To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultHardwareTaskOwner}
                                    onChange={(e) => handleRoutingChange('defaultHardwareTaskOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultHardwareTaskOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>
                        </Row>
                    </Col>

                    <h5 className="mt-2">
                        <i className="ri-arrow-right-s-fill font-size-22 align-middle" />
                        {t('Incident Reports')}
                    </h5>

                    <Col style={{
                        marginLeft: '24px'
                    }}>
                        <Row>
                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Assets To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultIncidentOwner}
                                    onChange={(e) => handleRoutingChange('defaultIncidentOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultIncidentOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>

                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Risks To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultIncidentRiskOwner}
                                    onChange={(e) => handleRoutingChange('defaultIncidentRiskOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultIncidentRiskOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>

                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Tasks To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultIncidentTaskOwner}
                                    onChange={(e) => handleRoutingChange('defaultIncidentTaskOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultIncidentTaskOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>
                        </Row>
                    </Col>

                    <h5 className="mt-2">
                        <i className="ri-arrow-right-s-fill font-size-22 align-middle" />
                        {t('Data Subject Requests')}
                    </h5>

                    <Col style={{
                        marginLeft: '24px'
                    }}>
                        <Row>
                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Assets To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultDsrOwner}
                                    onChange={(e) => handleRoutingChange('defaultDsrOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultDsrOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>

                            <Col sm='12' md="4" className="mb-3">
                                <Label>    
                                    {t("Assign Tasks To")}
                                </Label>
                                <Select
                                    options={analysts}
                                    isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                    value={routingSettings.defaultDsrTaskOwner}
                                    onChange={(e) => handleRoutingChange('defaultDsrTaskOwner', e)}
                                    placeholder={t("Select...")}
                                    isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                                />
                                {errors.defaultDsrTaskOwner && (
                                    <span className="invalid-feedback d-block">
                                        {t('This field is required')}
                                    </span>
                                )}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </ModalBody>

            <ModalFooter>
                <Button 
                    color="danger" 
                    onClick={toggle} 
                    disabled={false}
                >
                    {t('Cancel')}
                </Button>

                <Button 
                    color="primary" 
                    onClick={handleSaveSettings}
                    disabled={
                        saveSettingsMutation.isLoading || 
                        entitiesQuery.isFetching || 
                        fetchRoutingDetailsMutation.isLoading ||
                        analystsAreFetching
                    }
                >
                    {
                        saveSettingsMutation.isLoading && (
                            <Spinner 
                                animation="border" 
                                variant="primary" 
                                size="sm" 
                                className="me-1"
                            />
                        )
                    }
                    {t(isEditing ? 'Save' : 'Add Routing')}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

const mapStatetoProps = (state) => {
    const { user: currentUser } = state.Login;
    return {
        currentUser
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        RoutingModal
    )
);